import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import useNav from "./hooks/useNav";
import PrimaryButton from "./components/PrimaryButton";
import Header from "./components/Header";
import BackButton from "./components/BackButton";
import {
    CreditCard,
    DollarSign,
    Smartphone,
    Loader,
    Shield,
    AlertCircle,
    CheckCircle
} from "lucide-react";
import { useCart } from "./context/CartContext.jsx";
import { useRestaurant } from "./context/RestaurantContext.jsx";
import { useUser } from "./context/UserContext.jsx";
import { usePayment } from "./context/PaymentContext.jsx";
import appConfig from "./config";
import { toast } from "react-toastify";
import { useAuthFetch } from "./hooks/useAuthFetch";

const PaymentPage = () => {
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [providers, setProviders] = useState([]);
    const [loadingProviders, setLoadingProviders] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const navigate = useNav();
    const location = useLocation();
    const { totalPrice, cartItems, clearCart } = useCart();
    const { restaurant } = useRestaurant();

    // When coming from OrderWaitingPage the cart is already cleared (totalPrice = 0).
    // Use the order amount from router state as the authoritative value.
    const effectiveAmount = location.state?.order?.amount > 0
        ? location.state.order.amount
        : totalPrice;
    const { user, isAuthenticated } = useUser();
    const {
        loadRestaurantSettings,
        getAvailableProviders,
        getDefaultProvider,
        getProviderMethods,
        fullPaymentData,
        loadingSettings,
        hasActiveSettings,
        restaurantSettings
    } = usePayment();
    const { authFetch, loading: authLoading } = useAuthFetch();

    // Получаем данные заказа из location.state
    useEffect(() => {
        if (location.state?.order) {
            const order = location.state.order;
            setOrderData(order);
            // Сразу помечаем заказ как "ожидает оплаты", чтобы статус был корректным
            // даже если пользователь уйдёт со страницы оплаты до нажатия кнопки
            if (order?.id) {
                authFetch(`${appConfig.endpoint}/orders/${order.id}/awaiting-payment`, {
                    method: 'PUT',
                }).catch(() => {});
            }
        } else {
            toast.error("Данные заказа не найдены");
            navigate("/cart");
        }
    }, [location, navigate]);

    // Загружаем настройки платежей при загрузке с учетом суммы
    useEffect(() => {
        const loadSettings = async () => {
            if (restaurant?.id && effectiveAmount > 0) {
                setLoadingProviders(true);
                setLoadError(null);
                try {
                    await loadRestaurantSettings(restaurant.id, effectiveAmount);
                } catch (error) {
                    console.error('Error loading payment settings:', error);
                    setLoadError(error.message || 'Ошибка загрузки настроек оплаты');
                } finally {
                    setLoadingProviders(false);
                }
            }
        };

        loadSettings();
    }, [restaurant, effectiveAmount, loadRestaurantSettings]);

    // Обновляем список провайдеров из контекста
    useEffect(() => {
        if (fullPaymentData?.providers && fullPaymentData.providers.length > 0) {
            console.log('Providers loaded:', fullPaymentData.providers);
            setProviders(fullPaymentData.providers);

            // Устанавливаем провайдера по умолчанию
            const defaultProvider = getDefaultProvider() || fullPaymentData.providers[0];
            if (defaultProvider) {
                setSelectedProvider(defaultProvider.id);
                console.log('Default provider set:', defaultProvider);

                // Устанавливаем метод по умолчанию
                const defaultMethod = defaultProvider.methods?.find(m => m.is_default) || defaultProvider.methods?.[0];
                if (defaultMethod) {
                    setSelectedMethod(defaultMethod.method);
                    console.log('Default method set:', defaultMethod);
                }
            }
        } else if (fullPaymentData && (!fullPaymentData.providers || fullPaymentData.providers.length === 0)) {
            console.log('No providers available in fullPaymentData');
            setProviders([]);
        }
    }, [fullPaymentData, getDefaultProvider]);

    // Проверяем активность платежной системы
    useEffect(() => {
        if (restaurantSettings) {
            console.log('Restaurant settings:', restaurantSettings);
            console.log('Is payment active:', hasActiveSettings);
        }
    }, [restaurantSettings, hasActiveSettings]);

    const handleProceedToPayment = () => {
        if (!selectedMethod) {
            toast.error("Выберите способ оплаты");
            return;
        }

        if (!selectedProvider) {
            toast.error("Выберите платежную систему");
            return;
        }

        if (!orderData) {
            toast.error("Данные заказа не найдены");
            return;
        }

        if (selectedMethod === "cash") {
            handleCashPayment();
        } else if (selectedMethod === "card" || selectedMethod === "sbp") {
            handleOnlinePayment();
        }
    };

    const handleCashPayment = async () => {
        setIsProcessing(true);

        try {
            const response = await authFetch(`${appConfig.endpoint}/orders/${orderData.id}/update-payment`, {
                method: "PUT",
                body: JSON.stringify({
                    payment_method: "cash",
                    status: "confirmed"
                })
            });

            if (response.success) {
                toast.success("Заказ оформлен! Оплата при получении.");
                clearCart();
                navigate("/order-success", {
                    state: {
                        order: response.order || orderData,
                        paymentMethod: "cash"
                    }
                });
            } else {
                throw new Error(response.error || "Ошибка при обновлении заказа");
            }
        } catch (error) {
            console.error("Error updating order:", error);
            toast.error(error.message || "Ошибка при оформлении заказа");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleOnlinePayment = async () => {
        const selectedProviderData = providers.find(p => p.id === selectedProvider);
        const selectedMethodData = selectedProviderData?.methods.find(m => m.method === selectedMethod);

        navigate(`/payment-processing?slug=${restaurant.slug}`, {
            state: {
                order: orderData,
                paymentMethod: selectedMethod,
                provider: selectedProviderData,
                methodDetails: selectedMethodData,
                restaurantId: restaurant.id,
                amount: effectiveAmount
            }
        });
    };

    // Группируем методы по провайдерам для отображения
    const getMethodsByProvider = () => {
        const methods = [];

        providers.forEach(provider => {
            if (provider.methods && provider.methods.length > 0) {
                provider.methods.forEach(method => {
                    methods.push({
                        ...method,
                        providerId: provider.id,
                        providerName: provider.name,
                        providerLogo: provider.logo,
                        isDefaultProvider: provider.is_default,
                        providerIsActive: provider.is_active
                    });
                });
            }
        });

        // Сортируем: сначала методы от дефолтного провайдера, потом по приоритету
        return methods.sort((a, b) => {
            if (a.isDefaultProvider && !b.isDefaultProvider) return -1;
            if (!a.isDefaultProvider && b.isDefaultProvider) return 1;

            const aPriority = a.priority || 0;
            const bPriority = b.priority || 0;
            return bPriority - aPriority;
        });
    };

    const availableMethods = getMethodsByProvider().filter(m => m.method !== 'cash');

    // Иконки для методов
    const getMethodIcon = (method) => {
        switch (method) {
            case 'card': return CreditCard;
            case 'cash': return DollarSign;
            case 'sbp': return Smartphone;
            default: return CreditCard;
        }
    };

    if (!orderData) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <Loader className="animate-spin text-blue-500" size={32} />
            </div>
        );
    }

    // Состояние загрузки
    if (loadingSettings || loadingProviders) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Header />
                <div className="max-w-screen-md mx-auto px-4 pt-6">
                    <div className="bg-white rounded-lg p-8 text-center">
                        <Loader className="animate-spin text-blue-500 mx-auto mb-4" size={40} />
                        <p className="text-gray-600">Загрузка способов оплаты...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Ошибка загрузки
    if (loadError) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Header />
                <div className="max-w-screen-md mx-auto px-4 pt-6">
                    <div className="bg-white rounded-lg p-8 text-center">
                        <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">
                            Ошибка загрузки
                        </h2>
                        <p className="text-gray-600 mb-6">{loadError}</p>
                        <PrimaryButton onClick={() => navigate("/cart")}>
                            Вернуться в корзину
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        );
    }

    // Нет доступных способов оплаты
    if (availableMethods.length === 0) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Header />
                <div className="max-w-screen-md mx-auto px-4 pt-6">
                    <div className="bg-white rounded-lg p-8 text-center">
                        <AlertCircle className="text-yellow-500 mx-auto mb-4" size={48} />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">
                            Оплата временно недоступна
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Для этого ресторана пока не настроены способы оплаты
                        </p>
                        <PrimaryButton onClick={() => navigate("/cart")}>
                            Вернуться в корзину
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <Header />

            <div className="relative max-w-screen-md mx-auto px-4 pb-28 pt-6">
                {/* Назад + Заголовок */}
                <div className="flex items-center gap-3 mb-5">
                    <BackButton onClick={() => navigate(-1)} disabled={isProcessing} />
                    <h1 className="text-xl font-semibold text-gray-900">Способ оплаты</h1>
                </div>

                {/* Информация о заказе */}
                <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="font-medium text-gray-900 mb-1">Заказ #{orderData.id}</h3>
                            <p className="text-sm text-gray-600">
                                {cartItems.length} товар(ов)
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-gray-600">Сумма к оплате</p>
                            <p className="text-xl font-bold text-gray-900">{effectiveAmount} ₽</p>
                        </div>
                    </div>
                </div>

                {/* Отладка (только для разработки) */}
                {process.env.NODE_ENV === 'development' && (
                    <div className="bg-gray-100 rounded-lg p-4 mb-6 text-xs">
                        <details>
                            <summary className="cursor-pointer font-medium">Debug Info</summary>
                            <pre className="mt-2 overflow-auto max-h-40">
                                {JSON.stringify({
                                    hasActiveSettings,
                                    providersCount: providers.length,
                                    methodsCount: availableMethods.length,
                                    fullPaymentData
                                }, null, 2)}
                            </pre>
                        </details>
                    </div>
                )}

                {/* Выбор способа оплаты */}
                <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Выберите способ оплаты</h2>

                    {!hasActiveSettings && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                            <AlertCircle className="text-yellow-500 flex-shrink-0" size={18} />
                            <span className="text-sm text-yellow-800">
                                Платежная система может быть неактивна. Проверьте настройки в админке.
                            </span>
                        </div>
                    )}

                    <div className="space-y-4">
                        {availableMethods.map((method) => {
                            const Icon = getMethodIcon(method.method);
                            const isSelected = selectedMethod === method.method &&
                                selectedProvider === method.providerId;

                            // Показываем комиссию если есть
                            const commissionText = method.commission
                                ? `Комиссия ${method.commission}%`
                                : '';

                            // Показываем лимиты
                            const limitsText = [];
                            if (method.min_amount) limitsText.push(`от ${method.min_amount} ₽`);
                            if (method.max_amount) limitsText.push(`до ${method.max_amount} ₽`);
                            const limits = limitsText.join(' ');

                            return (
                                <div
                                    key={`${method.providerId}-${method.method}`}
                                    onClick={() => {
                                        setSelectedMethod(method.method);
                                        setSelectedProvider(method.providerId);
                                    }}
                                    className={`p-4 border-2 rounded-lg transition-all cursor-pointer hover:shadow-sm ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3 flex-1">
                                            <div className={`p-2 rounded-full ${
                                                isSelected ? 'bg-blue-100' : 'bg-gray-100'
                                            }`}>
                                                <Icon
                                                    size={20}
                                                    className={
                                                        isSelected ? 'text-blue-600' : 'text-gray-600'
                                                    }
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-gray-900">
                                                        {method.name}
                                                    </span>
                                                    {method.providerLogo && (
                                                        <img
                                                            src={method.providerLogo}
                                                            alt={method.providerName}
                                                            className="h-4 w-auto opacity-50"
                                                        />
                                                    )}
                                                    {!method.providerIsActive && (
                                                        <Tag color="orange" className="ml-2">Неактивен</Tag>
                                                    )}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {method.description}
                                                    {commissionText && (
                                                        <span className="ml-2 text-xs text-gray-400">
                                                            {commissionText}
                                                        </span>
                                                    )}
                                                </div>
                                                {limits && (
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        {limits}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ml-4 ${
                                            isSelected
                                                ? 'border-blue-500 bg-blue-500'
                                                : 'border-gray-300'
                                        }`}>
                                            {isSelected && (
                                                <div className="w-2 h-2 rounded-full bg-white" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Информация о безопасности */}
                    {selectedMethod === "card" && (
                        <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center">
                                <Shield className="text-green-500 mr-2 flex-shrink-0" size={18} />
                                <span className="text-sm text-green-800">
                                    Платеж защищен технологиями платежных систем и 3D-Secure
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Информация о ресторане */}
                <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
                    <h3 className="font-medium text-gray-900 mb-2">Информация о заказе</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                            <span>Ресторан:</span>
                            <span className="font-medium">{restaurant?.name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Тип заказа:</span>
                            <span className="font-medium">
                                {orderData.delivery ? "Доставка" : "В ресторане"}
                            </span>
                        </div>
                        {orderData.countPerson > 1 && (
                            <div className="flex justify-between">
                                <span>Количество персон:</span>
                                <span className="font-medium">{orderData.countPerson}</span>
                            </div>
                        )}
                        {orderData.note && (
                            <div className="mt-2 p-2 bg-gray-50 rounded">
                                <span className="font-medium">Комментарий:</span>
                                <p className="mt-1">{orderData.note}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Фиксированная кнопка */}
            <div className="fixed bottom-0 left-0 right-0 bg-white px-4 py-3 shadow-md z-10">
                <div className="max-w-screen-md mx-auto flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600">Итого к оплате</p>
                        <p className="text-lg font-semibold text-gray-900">{effectiveAmount} ₽</p>
                    </div>
                    <PrimaryButton
                        onClick={handleProceedToPayment}
                        disabled={isProcessing || loadingProviders || loadingSettings || !selectedMethod || !selectedProvider || authLoading}
                        className="min-w-[160px]"
                    >
                        {isProcessing ? (
                            <Loader className="animate-spin mr-2" size={20} />
                        ) : selectedMethod === "cash" ? (
                            "Подтвердить заказ"
                        ) : (
                            "Перейти к оплате"
                        )}
                    </PrimaryButton>
                </div>
            </div>
        </div>
    );
};

export default PaymentPage;